import React, { useMemo, useEffect, useRef } from 'react';
import { getDataByPath } from '../../utils/dataHelpers';
import { getAvailableFields } from '../../utils/widgetHelpers';
import SearchableSelect from '../SearchableSelect';

// Table Column Configuration Component
// NOTE: This component directly uses block.dataConfig.tableColumns instead of local state
// to avoid synchronization issues between local state and parent state
const TableColumnConfiguration = ({ block, scanData, onUpdateColumns, dataContext, onFlattenFieldChange, onDataConfigUpdate }) => {
    // Directly compute columns from props - no memoization to ensure fresh data
    const savedColumns = block.dataConfig.tableColumns || [];
    // Ensure columns have stable IDs for React keys and matching
    const columns = useMemo(() => {
        return savedColumns.map((col, idx) => ({
            ...col,
            id: col.id || `col-idx-${idx}`
        }));
    }, [savedColumns]);

    const metricPath = block.dataConfig?.metricPath;
    const prevMetricPathRef = useRef(metricPath);

    // Get available fields from the selected data source
    const { availableFields, lookoutSubFields } = useMemo(() => {
        if (!metricPath || !scanData) return { availableFields: [], lookoutSubFields: [] };
        const baseFields = getAvailableFields(scanData, metricPath);
        
        const flattenField = block.dataConfig?.flattenField;
        if (!flattenField) return { availableFields: baseFields, lookoutSubFields: [] };
        
        // When a lookout/flatten field is active, also discover sub-fields from the nested items
        const dataSource = dataContext || scanData;
        const rawData = dataSource ? getDataByPath(dataSource, metricPath) : null;
        if (!rawData || !Array.isArray(rawData)) return { availableFields: baseFields, lookoutSubFields: [] };
        
        const subFieldSet = new Set();
        
        const samplesToScan = Math.min(rawData.length, 50);
        for (let i = 0; i < samplesToScan; i++) {
            const item = rawData[i];
            if (!item || typeof item !== 'object') continue;

            // Navigate to the flatten target (handles keys with spaces via getDataByPath fallback)
            const parts = flattenField.split('.');
            let target = item;
            for (const part of parts) {
                if (!target || typeof target !== 'object') { target = null; break; }
                // Try exact key first, then case-insensitive, then space-join remaining
                if (target[part] !== undefined) {
                    target = target[part];
                } else {
                    // Try combining multiple parts as a spaced key
                    let found = false;
                    const keys = Object.keys(target);
                    for (const k of keys) {
                        if (k.toLowerCase() === part.toLowerCase()) {
                            target = target[k];
                            found = true;
                            break;
                        }
                    }
                    if (!found) { target = null; break; }
                }
            }
            if (!target) continue;
            
            if (Array.isArray(target)) {
                for (let ai = 0; ai < Math.min(target.length, 5); ai++) {
                    const arrItem = target[ai];
                    if (arrItem && typeof arrItem === 'object' && !Array.isArray(arrItem)) {
                        const collectSubFields = (obj, prefix, depth = 0) => {
                            if (depth > 3 || !obj || typeof obj !== 'object') return;
                            Object.keys(obj).forEach(key => {
                                if (key.startsWith('_')) return;
                                const subPath = prefix ? `${prefix}.${key}` : key;
                                subFieldSet.add(subPath); // plain path e.g. "Computer Name" or "nested.key"
                                const val = obj[key];
                                if (val && typeof val === 'object' && !Array.isArray(val)) {
                                    collectSubFields(val, subPath, depth + 1);
                                }
                            });
                        };
                        collectSubFields(arrItem, '');
                    }
                }
            }
        }
        
        const lookoutSubFields = Array.from(subFieldSet).sort();
        // Parent fields: filter to only top-level non-nested ones (exclude the lookout field path itself)
        const parentFields = baseFields.filter(f => !f.startsWith('_flattenedItem'));
        return { availableFields: parentFields, lookoutSubFields };
    }, [metricPath, scanData, block.dataConfig?.flattenField, dataContext]);

    const isDateField = (path) => {
        if (!path) return false;
        const cleanPath = String(path).split('|')[0].trim();
        const lower = cleanPath.toLowerCase();
        return lower.includes('date') || lower.includes('time') || lower.includes('logon') || 
               lower.includes('timestamp') || lower.includes('created') || lower.includes('modified');
    };

    const addColumn = () => {
        const currentColumns = block.dataConfig.tableColumns || [];
        const newColumn = {
            id: `col-${Date.now()}`,
            header: `Column ${currentColumns.length + 1}`,
            fieldPath: ''
        };
        const updatedColumns = [...currentColumns, newColumn];
        onUpdateColumns(updatedColumns);
    };

    const removeColumn = (columnId) => {
        const currentColumns = block.dataConfig.tableColumns || [];
        const colToRemove = currentColumns.find(col => col.id === columnId);
        const updatedColumns = currentColumns.filter(col => col.id !== columnId);
        
        let newFlattenField = block.dataConfig.flattenField;
        if (colToRemove?.columnType === 'lookout') {
            newFlattenField = '';
        }

        if (onDataConfigUpdate) {
            onDataConfigUpdate(block.id, {
                ...block.dataConfig,
                tableColumns: updatedColumns,
                flattenField: newFlattenField
            });
        } else {
            onUpdateColumns(updatedColumns);
            if (colToRemove?.columnType === 'lookout' && onFlattenFieldChange) {
                onFlattenFieldChange('');
            }
        }
    };

    const updateColumn = (columnId, field, value) => {
        const updatedColumns = columns.map(col =>
            col.id === columnId ? { ...col, [field]: value } : col
        );
        if (onDataConfigUpdate) {
            onDataConfigUpdate(block.id, {
                ...block.dataConfig,
                tableColumns: updatedColumns
            });
        } else {
            onUpdateColumns(updatedColumns);
        }
    };

    const updateColumnFields = (columnId, updates) => {
        const updatedColumns = columns.map(col =>
            col.id === columnId ? { ...col, ...updates } : col
        );
        if (onDataConfigUpdate) {
            onDataConfigUpdate(block.id, {
                ...block.dataConfig,
                tableColumns: updatedColumns
            });
        } else {
            onUpdateColumns(updatedColumns);
        }
    };

    const autoDetectColumns = (isAutoTrigger = false) => {
        if (!metricPath) {
            console.warn('[autoDetectColumns] No metricPath configured');
            if (!isAutoTrigger) alert('Please select a Data Source first before auto-detecting columns.');
            return;
        }

        // Use dataContext instead of scanData for consistency with getBlockData
        const dataSource = dataContext || scanData;
        console.log('[autoDetectColumns] Using data source:', { 
            hasDataContext: !!dataContext, 
            hasScanData: !!scanData,
            metricPath 
        });
        
        if (!dataSource) {
            console.warn('[autoDetectColumns] No data source available');
            if (!isAutoTrigger) alert('No scan data available. Please ensure scan data is loaded.');
            return;
        }

        // Get the actual data that will be displayed in the table
        let data = getDataByPath(dataSource, metricPath);
        console.log('[autoDetectColumns] Data from path:', { 
            metricPath, 
            dataType: data === null ? 'null' : data === undefined ? 'undefined' : Array.isArray(data) ? 'array' : typeof data,
            dataLength: Array.isArray(data) ? data.length : (data && typeof data === 'object' ? Object.keys(data).length : 0),
            data 
        });
        
        // Handle null/undefined data
        if (data === null || data === undefined) {
            console.warn('[autoDetectColumns] No data found at path:', metricPath);
            if (!isAutoTrigger) alert(`No data found at path "${metricPath}". Please check your Data Source selection.`);
            return;
        }
        
        // Handle object data - convert to array format for table display
        if (typeof data === 'object' && !Array.isArray(data)) {
            console.log('[autoDetectColumns] Handling object data with', Object.keys(data).length, 'keys');
            // For objects, create two columns: Property and Value
            const newColumns = [
                {
                    id: `col-auto-${Date.now()}-0`,
                    header: 'Property',
                    fieldPath: 'key'
                },
                {
                    id: `col-auto-${Date.now()}-1`,
                    header: 'Value',
                    fieldPath: 'value'
                }
            ];
            console.log('[autoDetectColumns] Created columns for object data:', newColumns);
            onUpdateColumns(newColumns);
            return;
        }
        
        if (!Array.isArray(data) || data.length === 0) {
            console.warn('[autoDetectColumns] Data is not a valid array:', typeof data);
            if (!isAutoTrigger) alert(`Data at "${metricPath}" is not a valid array or is empty. Cannot auto-detect columns.`);
            return;
        }

        // Apply filters if they exist to get the actual filtered data
        if (block.dataConfig.deviceFilters && block.dataConfig.deviceFilters.length > 0) {
            console.log('[autoDetectColumns] Applying filters:', block.dataConfig.deviceFilters);
            
            // Import filterTableData logic inline to avoid circular dependencies
            const evaluateCondition = (itemValue, operator, targetValue) => {
                const normalize = (val) => String(val).toLowerCase();
                if (operator === 'is_empty') return itemValue === undefined || itemValue === null || itemValue === '';
                if (operator === 'is_not_empty') return itemValue !== undefined && itemValue !== null && itemValue !== '';
                const sItem = normalize(itemValue ?? '');
                const sTarget = normalize(targetValue ?? '');
                switch (operator) {
                    case 'equals': return sItem === sTarget;
                    case 'not_equals': return sItem !== sTarget;
                    case 'contains': return sItem.includes(sTarget);
                    case 'not_contains': return !sItem.includes(sTarget);
                    case 'starts_with': return sItem.startsWith(sTarget);
                    case 'ends_with': return sItem.endsWith(sTarget);
                    case 'greater_than': return parseFloat(itemValue) > parseFloat(targetValue);
                    case 'less_than': return parseFloat(itemValue) < parseFloat(targetValue);
                    default: return true;
                }
            };

            const getNestedField = (obj, path) => {
                if (!obj || !path) return null;
                if (obj[path] !== undefined) return obj[path];
                const parts = path.split('.');
                let current = obj;
                for (const key of parts) {
                    if (current === null || current === undefined) return null;
                    current = current[key];
                }
                return current;
            };

            data = data.filter(item => {
                const filters = block.dataConfig.deviceFilters;
                if (filters.length === 0) return true;
                let result = evaluateCondition(
                    getNestedField(item, filters[0].fieldPath),
                    filters[0].operator,
                    filters[0].value
                );
                for (let i = 1; i < filters.length; i++) {
                    const filter = filters[i];
                    const match = evaluateCondition(
                        getNestedField(item, filter.fieldPath),
                        filter.operator,
                        filter.value
                    );
                    if (filter.logic === 'OR') {
                        result = result || match;
                    } else {
                        result = result && match;
                    }
                }
                return result;
            });
            
            console.log('[autoDetectColumns] Filtered data length:', data.length);
        }

        // Get fields from the actual filtered data
        if (data.length === 0) {
            console.warn('[autoDetectColumns] No data remaining after filtering');
            return;
        }
        
        const firstItem = data[0];
        console.log('[autoDetectColumns] First item type:', typeof firstItem, firstItem);

        // Define known schemas for array-of-arrays data
        const KNOWN_SCHEMAS = {
            'findings': ['Name', 'Status', 'Score', 'Message', 'Cost'],
            'open_ports_summary': ['Total Devices', 'Affected Devices', 'Percentage'],
            // Add more as discovered
        };

        // Handle primitive array (e.g. ['a', 'b']) or null/undefined
        if (typeof firstItem !== 'object' || firstItem === null) {
            console.log('[autoDetectColumns] Primitive array detected');
            const newColumns = [{
                id: `col-auto-${Date.now()}`,
                header: 'Value',
                fieldPath: '' 
            }];
            console.log('[autoDetectColumns] Created single column for primitive array:', newColumns);
            onUpdateColumns(newColumns);
            return;
        }

        // --- LOOKOUT FIELD HANDLING ---
        // When a flattenField is active, each item has a _flattenedItem containing the nested sub-object.
        // Auto-detect should prioritise the sub-fields rather than the parent device-level fields.
        // NOTE: The render layer (processTableData) already checks item._flattenedItem automatically,
        //       so column fieldPaths should be plain sub-field names (e.g. 'Computer Name'),
        //       NOT '_flattenedItem.Computer Name'. The render will find them via _flattenedItem first,
        //       then fall back to the top-level item if not found there.
        const flattenField = block.dataConfig?.flattenField;
        if (flattenField && firstItem._flattenedItem && typeof firstItem._flattenedItem === 'object') {
            const subItem = firstItem._flattenedItem;
            const subKeys = Object.keys(subItem).filter(k => !k.startsWith('_'));
            
            if (subKeys.length > 0) {
                console.log('[autoDetectColumns] Lookout Field active – using sub-fields:', subKeys);
                const newColumns = subKeys.slice(0, 10).map((field, idx) => ({
                    id: `col-auto-${Date.now()}-${idx}`,
                    header: field,
                    // Use plain field name – the render engine resolves it from _flattenedItem automatically
                    fieldPath: field
                }));
                console.log('[autoDetectColumns] Created columns from Lookout Field sub-fields:', newColumns);
                onUpdateColumns(newColumns);
                return;
            }
        }
        // --- END LOOKOUT FIELD HANDLING ---

        // Get all keys from the first item, excluding internal fields
        const TABLE_INTERNAL_FIELDS = ['_parent', '_parentIp', '_parentHostname', '__isPlaceholder__', '_flattenedItem', '_flattenedKey', '_flattenedValue'];
        let allKeys = Object.keys(firstItem).filter(key => 
            !TABLE_INTERNAL_FIELDS.includes(key) && !key.startsWith('_')
        );

        console.log('[autoDetectColumns] Detected fields:', allKeys);

        // Check if we have a known schema for this metricPath
        const normalizedPath = metricPath.toLowerCase();
        let headersToUse = null;

        if (normalizedPath.includes('findings')) {
             const schema = KNOWN_SCHEMAS['findings'];
             // Verify if data structure roughly matches (array index keys vs schema length)
             // Findings is array of arrays, so keys are "0", "1", "2"...
             // If allKeys contains "0", "1", it's likely an array
             const areKeysNumeric = allKeys.every(k => !isNaN(parseInt(k)));
             if (areKeysNumeric && schema) {
                 console.log('[autoDetectColumns] Matched known schema for findings:', schema);
                 headersToUse = schema;
             }
        }

        if (allKeys.length === 0) {
            console.warn('[autoDetectColumns] No suitable fields found in first item');
            if (!isAutoTrigger) alert('Could not detect any suitable columns from the data.');
            return;
        }

        // Prefer top-level fields, limit to reasonable number
        const fieldsToUse = allKeys.slice(0, 10);

        const newColumns = fieldsToUse.map((field, idx) => {
            let header = field;
            // Apply known schema header if available and index matches
            if (headersToUse && headersToUse[idx]) {
                header = headersToUse[idx];
            }
            
            return {
                id: `col-auto-${Date.now()}-${idx}`,
                header: header,
                fieldPath: field
            };
        });


        console.log('[autoDetectColumns] Creating columns:', newColumns);
        onUpdateColumns(newColumns);
    };

    // Auto-detect columns when data source or lookout field changes and no columns are configured
    const prevFlattenFieldRef = useRef(block.dataConfig?.flattenField);
    useEffect(() => {
        const flattenField = block.dataConfig?.flattenField;
        const metricPathChanged = metricPath !== prevMetricPathRef.current;
        const flattenFieldChanged = flattenField !== prevFlattenFieldRef.current;

        if (metricPathChanged) {
            prevMetricPathRef.current = metricPath;
        }
        if (flattenFieldChanged) {
            prevFlattenFieldRef.current = flattenField;
        }
        
        // Trigger if either changed AND columns are empty (DataTab resets them on change)
        if ((metricPathChanged || flattenFieldChanged) && metricPath && columns.length === 0) {
            const reason = metricPathChanged ? 'new data source' : 'new lookout field';
            console.log('[TableColumnConfiguration] Auto-triggering column detection for', reason, ':', metricPath, '/', flattenField);
            // Small timeout to allow data context to update if needed
            setTimeout(() => {
                autoDetectColumns(true);
            }, 150);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [metricPath, block.dataConfig?.flattenField, columns.length]); // Depend on metricPath, flattenField and columns length

    return (
        <div className="table-column-config">
            <div className="property-field" style={{ marginTop: '1rem' }}>
                <label>Table Columns</label>
                <small className="help-text" style={{ display: 'block', marginBottom: '0.5rem' }}>
                    Map scan result fields to table columns. Each column will display data from the selected field.
                </small>

                {columns.length === 0 && (
                    <div className="empty-state" style={{
                        padding: '1rem',
                        textAlign: 'center',
                        color: 'var(--text-secondary)',
                        border: '1px dashed var(--border)',
                        borderRadius: 'var(--radius)',
                        marginBottom: '0.5rem'
                    }}>
                        No columns configured. Click "Auto-detect Columns" to automatically populate, or "Add Column" to manually configure.
                    </div>
                )}

                {columns.map((column, index) => (
                    <div key={column.id || index} className="column-config-item" style={{
                        marginBottom: '0.75rem',
                        padding: '0.75rem',
                        border: '1px solid var(--border)',
                        borderRadius: 'var(--radius)',
                        backgroundColor: 'var(--background)'
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                            <strong style={{ fontSize: '0.875rem' }}>Column {index + 1}</strong>
                            <button
                                type="button"
                                onClick={() => removeColumn(column.id)}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    color: 'var(--danger)',
                                    cursor: 'pointer',
                                    fontSize: '1.25rem',
                                    padding: '0',
                                    width: '24px',
                                    height: '24px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                                title="Remove column"
                            >
                                ×
                            </button>
                        </div>

                        <div style={{ marginBottom: '0.5rem' }}>
                            <label style={{ fontSize: '0.75rem', display: 'block', marginBottom: '0.25rem' }}>
                                Column Header
                            </label>
                            <input
                                type="text"
                                value={column.header || ''}
                                onChange={(e) => updateColumn(column.id, 'header', e.target.value)}
                                placeholder="Column Name"
                                style={{
                                    width: '100%',
                                    padding: '0.5rem',
                                    border: '1px solid var(--border)',
                                    borderRadius: '4px',
                                    fontSize: '0.875rem'
                                }}
                            />
                        </div>



                        <div style={{ marginBottom: '0.5rem' }}>
                            <label style={{ fontSize: '0.75rem', display: 'block', marginBottom: '0.25rem' }}>
                                Column Type
                            </label>
                            <select
                                value={column.columnType || 'field'}
                                onChange={(e) => {
                                    const newType = e.target.value;
                                    const updates = { columnType: newType };
                                    let newFlattenField = block.dataConfig?.flattenField;
                                    
                                    if (newType === 'lookup') {
                                        updates.fieldPath = '';
                                        updates.lookupSource = 'port_to_service';
                                        updates.sourceField = '_flattenedKey';
                                    } else if (newType === 'conditional') {
                                        if (!column.conditionalFields) {
                                            updates.conditionalFields = [''];
                                        }
                                    } else if (newType === 'lookout') {
                                        updates.fieldPath = block.dataConfig?.flattenField || '';
                                    } else {
                                        // Reset lookup/conditional/lookout fields when switching to 'field'
                                        updates.lookupSource = undefined;
                                        updates.sourceField = undefined;
                                        // If the current column WAS the lookout, clear the flattenField
                                        if (column.columnType === 'lookout') {
                                            newFlattenField = '';
                                        }
                                    }
                                    
                                    // Atomic update if possible
                                    if (onDataConfigUpdate) {
                                        const newColumns = columns.map(col => {
                                            if (col.id === column.id) {
                                                return { ...col, ...updates };
                                            }
                                            // Ensure only one lookout field exists - reset others to 'field'
                                            if (newType === 'lookout' && col.columnType === 'lookout') {
                                                return { ...col, columnType: 'field' };
                                            }
                                            return col;
                                        });
                                        onDataConfigUpdate(block.id, {
                                            ...block.dataConfig,
                                            tableColumns: newColumns,
                                            flattenField: newFlattenField
                                        });
                                    } else {
                                        updateColumnFields(column.id, updates);
                                        if (column.columnType === 'lookout' && newType !== 'lookout' && onFlattenFieldChange) {
                                            onFlattenFieldChange('');
                                        }
                                    }
                                }}
                                style={{
                                    width: '100%',
                                    padding: '0.5rem',
                                    border: '1px solid var(--border)',
                                    borderRadius: '4px',
                                    fontSize: '0.875rem',
                                    cursor: 'pointer'
                                }}
                            >
                                <option value="field">Data Field</option>
                                <option value="lookout">Lookout Field</option>
                                <option value="conditional">Conditional Status</option>
                            </select>
                        </div>

                        {/* Lookout Field selector - shown for 'lookout' type */}
                        {column.columnType === 'lookout' && (() => {
                            // Discover nested arrays from the data source
                            const dataSource = dataContext || scanData;
                            const rawData = block.dataConfig?.metricPath
                                ? (dataSource ? getDataByPath(dataSource, block.dataConfig.metricPath) : null)
                                : null;

                            // Build a map of arrayPath → [primitive sub-field names]
                            const arrayMap = {}; // { 'system.Logical Disks': ['Computer Name', 'Drive Name', ...] }
                            if (rawData && Array.isArray(rawData)) {
                                const samplesToScan = Math.min(rawData.length, 20);
                                for (let i = 0; i < samplesToScan; i++) {
                                    const item = rawData[i];
                                    if (!item || typeof item !== 'object') continue;
                                    const scan = (obj, prefix, depth) => {
                                        if (depth > 4) return;
                                        Object.keys(obj).forEach(key => {
                                            if (key.startsWith('_')) return;
                                            const val = obj[key];
                                            const path = prefix ? `${prefix}.${key}` : key;
                                            if (Array.isArray(val) && val.length > 0) {
                                                if (!arrayMap[path]) arrayMap[path] = new Set();
                                                // Collect primitive sub-fields from first few items
                                                for (let ai = 0; ai < Math.min(val.length, 5); ai++) {
                                                    const arrItem = val[ai];
                                                    if (arrItem && typeof arrItem === 'object' && !Array.isArray(arrItem)) {
                                                        Object.keys(arrItem).forEach(subKey => {
                                                            if (!subKey.startsWith('_') && typeof arrItem[subKey] !== 'object') {
                                                                arrayMap[path].add(subKey);
                                                            }
                                                        });
                                                        // Recurse into sub-arrays too
                                                        scan(arrItem, path, depth + 1);
                                                    }
                                                }
                                            } else if (val && typeof val === 'object' && !Array.isArray(val)) {
                                                scan(val, path, depth + 1);
                                            }
                                        });
                                    };
                                    scan(item, '', 0);
                                }
                            }

                            // Build grouped options: whole-item + sub-fields for each array
                            const lookoutOptions = [];
                            Object.keys(arrayMap).sort().forEach(arrayPath => {
                                // Group header
                                lookoutOptions.push({
                                    label: `── ${arrayPath} ──`,
                                    value: `__group__${arrayPath}`,
                                    disabled: true
                                });
                                // Whole-item option (existing behavior)
                                lookoutOptions.push({
                                    label: `   └─ ${arrayPath} (whole item)`,
                                    value: arrayPath
                                });
                                // Sub-field options
                                const subFields = Array.from(arrayMap[arrayPath]).sort();
                                subFields.forEach(subField => {
                                    lookoutOptions.push({
                                        label: `   └─ ${arrayPath}.${subField}`,
                                        value: `${arrayPath}.${subField}`
                                    });
                                });
                            });

                            // Determine if current fieldPath is a sub-field or whole-item selection
                            const currentPath = column.fieldPath || '';
                            const matchedArray = Object.keys(arrayMap).find(ap =>
                                currentPath === ap || currentPath.startsWith(ap + '.')
                            );
                            const isSubField = matchedArray && currentPath !== matchedArray;
                            const subFieldName = isSubField ? currentPath.slice(matchedArray.length + 1) : null;

                            return (
                                <div style={{
                                    padding: '0.75rem',
                                    border: '1px dashed #6366f1',
                                    borderRadius: 'var(--radius)',
                                    backgroundColor: 'rgba(99,102,241,0.04)',
                                    marginTop: '0.5rem'
                                }}>
                                    <label style={{ fontSize: '0.75rem', display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', color: '#6366f1' }}>
                                        🔍 Lookout Field
                                    </label>
                                    <small style={{ display: 'block', marginBottom: '0.5rem', color: '#6b7280', fontSize: '0.7rem' }}>
                                        Pick an array for full-item rows, or a specific field (e.g. <code>system.Logical Disks.Drive Name</code>) to show only that value per row.
                                    </small>
                                    <SearchableSelect
                                        value={currentPath}
                                        onChange={(newValue) => {
                                            if (!newValue || newValue.startsWith('__group__')) return;
                                            
                                            // Determine the array prefix to set as flattenField
                                            const arrPath = Object.keys(arrayMap).find(ap =>
                                                newValue === ap || newValue.startsWith(ap + '.')
                                            ) || newValue;

                                            // Atomic update: Set both the column's fieldPath AND the widget's flattenField at once
                                            if (onDataConfigUpdate) {
                                                const newColumns = columns.map(col => 
                                                    col.id === column.id ? { ...col, fieldPath: newValue } : col
                                                );
                                                onDataConfigUpdate(block.id, {
                                                    ...block.dataConfig,
                                                    tableColumns: newColumns,
                                                    flattenField: arrPath
                                                });
                                            } else {
                                                // Fallback to separate updates if prop not present
                                                updateColumn(column.id, 'fieldPath', newValue);
                                                if (onFlattenFieldChange) onFlattenFieldChange(arrPath);
                                            }
                                        }}
                                        placeholder="Select array or sub-field..."
                                        options={lookoutOptions.length > 0 ? lookoutOptions : [
                                            { label: '(No nested arrays detected – select a Data Source first)', value: '', disabled: true }
                                        ]}
                                    />
                                    {currentPath && (
                                        <small style={{ display: 'block', marginTop: '0.4rem', color: '#6b7280', fontSize: '0.7rem' }}>
                                            {isSubField
                                                ? <>✅ Rows expand from <strong>"{matchedArray}"</strong>, showing <strong>"{subFieldName}"</strong> per row.</>
                                                : <>✅ Rows expand from <strong>"{currentPath}"</strong> — each item becomes a row. Use <em>Auto-detect Columns</em> to get sub-fields.</>
                                            }
                                        </small>
                                    )}
                                </div>
                            );
                        })()}
                        {/* Data Field selector - shown for 'field' type */}
                        {(!column.columnType || column.columnType === 'field') && (
                        <div>
                            <label style={{ fontSize: '0.75rem', display: 'block', marginBottom: '0.25rem' }}>
                                Data Field {availableFields.some(f => f.includes('.')) && <span style={{ color: 'var(--text-secondary)', fontWeight: 'normal' }}>(nested fields available)</span>}
                            </label>
                            
                            {/* Prepare options with grouping for SearchableSelect */}
                            <SearchableSelect
                                value={column.fieldPath || ''}
                                onChange={(newValue) => {
                                    const updates = { fieldPath: newValue };
                                    // Clear dateFormat if switching to a non-date field
                                    if (!isDateField(newValue)) {
                                        updates.dateFormat = '';
                                    }
                                    updateColumnFields(column.id, updates);
                                }}
                                placeholder="Select a field or enter path..."
                                options={(() => {
                                    // Group fields by their parent path for better organization
                                    const topLevelFields = availableFields.filter(f => !f.includes('.'));
                                    const nestedFields = availableFields.filter(f => f.includes('.'));

                                    // Group nested fields by their parent
                                    const groupedNested = {};
                                    nestedFields.forEach(field => {
                                        const parts = field.split('.');
                                        const parent = parts.slice(0, -1).join('.');
                                        if (!groupedNested[parent]) {
                                            groupedNested[parent] = [];
                                        }
                                        groupedNested[parent].push(field);
                                    });

                                    const options = [];

                                    // Add Lookout sub-fields first if a lookout/flatten is active
                                    if (block.dataConfig?.flattenField && lookoutSubFields.length > 0) {
                                        const flattenField = block.dataConfig.flattenField;
                                        const lookoutLabel = flattenField.split('.').pop();
                                        options.push({
                                            label: `── ${lookoutLabel} Sub-fields ──`,
                                            value: 'group-lookout-subfields',
                                            disabled: true
                                        });
                                        lookoutSubFields.forEach(field => {
                                            // Show full path e.g. "system.Logical Disks.Computer Name"
                                            const fullPath = `${flattenField}.${field}`;
                                            options.push({
                                                label: `   └─ ${fullPath}`,
                                                value: field // plain sub-field name for _flattenedItem resolution
                                            });
                                        });
                                    }

                                    // Add top-level fields first
                                    if (topLevelFields.length > 0) {
                                        if (block.dataConfig?.flattenField) {
                                            options.push({
                                                label: '── Parent Device Fields ──',
                                                value: 'group-parent',
                                                disabled: true
                                            });
                                        }
                                        topLevelFields.forEach(field => {
                                            options.push({
                                                label: block.dataConfig?.flattenField ? `   └─ ${field}` : field,
                                                value: field
                                            });
                                        });
                                    }

                                    // Add nested fields grouped by parent — show full path in label for disambiguation
                                    if (Object.keys(groupedNested).length > 0) {
                                        Object.keys(groupedNested).sort().forEach(parent => {
                                            options.push({
                                                label: `── ${parent} ──`,
                                                value: `group-${parent}`,
                                                disabled: true
                                            });
                                            groupedNested[parent].sort().forEach(field => {
                                                // Show full path (e.g. "system.Logical Disks.Computer Name")
                                                // so results are unique when the user searches
                                                options.push({
                                                    label: `   └─ ${field}`,
                                                    value: field
                                                });
                                            });
                                        });
                                    }
                                    return options;
                                })()}
                            />

                            <div style={{ 
                                marginTop: '0.4rem', 
                                marginBottom: '0.4rem',
                                color: 'var(--text-secondary)', 
                                fontSize: '0.7rem' 
                            }}>
                                {availableFields.some(f => f.includes('.')) && (
                                    <span>💡 Nested fields (with dots) allow accessing child properties. </span>
                                )}
                                <span>Advanced: use <code>| format</code> for custom <code>date-fns</code> strings.</span>
                            </div>

                            {/* Date Format dropdown - visible for date-like fields */}
                            {isDateField(column.fieldPath) && (
                                <div style={{ 
                                    marginTop: '0.75rem', 
                                    padding: '0.6rem 0.75rem', 
                                    backgroundColor: 'rgba(52, 152, 219, 0.08)', 
                                    borderLeft: '3px solid #3498db', 
                                    borderRadius: '0 4px 4px 0' 
                                }}>
                                    <label style={{ fontSize: '0.75rem', display: 'block', marginBottom: '0.4rem', fontWeight: 'bold', color: '#2c3e50' }}>
                                        📅 Date Display Format
                                    </label>
                                    <select
                                        value={column.dateFormat || ''}
                                        onChange={(e) => updateColumn(column.id, 'dateFormat', e.target.value)}
                                        style={{
                                            width: '100%',
                                            padding: '0.4rem',
                                            border: '1px solid var(--border)',
                                            borderRadius: '4px',
                                            fontSize: '0.875rem',
                                            backgroundColor: 'white'
                                        }}
                                    >
                                        <option value="">Default (RAW)</option>
                                        <option value="date">Date only (YYYY-MM-DD)</option>
                                        <option value="time">Time only (HH:mm:ss)</option>
                                        <option value="datetime">Date & Time (YYYY-MM-DD HH:mm:ss)</option>
                                        <option value="MMMM d, yyyy hh:mm a">Full (January 1, 2026 01:00 PM)</option>
                                        <option value="MMM d, yyyy hh:mm a">Abbrev. (Jan 1, 2026 01:00 PM)</option>
                                        <option value="MMMM d, yyyy HH:mm">Full 24h (January 1, 2026 13:00)</option>
                                        <option value="MMMM d, yyyy">Full Date (January 1, 2026)</option>
                                    </select>
                                </div>
                            )}
                        </div>
                        )}


                        {/* Conditional Status configuration - shown for 'conditional' type */}
                        {column.columnType === 'conditional' && (
                        <div style={{ 
                            padding: '0.75rem', 
                            border: '1px dashed var(--border)', 
                            borderRadius: 'var(--radius)', 
                            backgroundColor: 'rgba(0,0,0,0.02)',
                            marginTop: '0.5rem'
                        }}>
                            <label style={{ fontSize: '0.75rem', display: 'block', marginBottom: '0.75rem', fontWeight: 'bold', color: 'var(--primary)' }}>
                                Conditional Status Rules
                            </label>
                            
                            <div style={{ marginBottom: '1rem' }}>
                                <label style={{ fontSize: '0.7rem', display: 'block', marginBottom: '0.25rem', color: 'var(--text-secondary)' }}>
                                    Fields to monitor (e.g. Domain, Private, Public)
                                </label>
                                {(column.conditionalFields || ['']).map((fieldData, fIdx) => {
                                    // Support both simple string paths and object-based rules
                                    const isObject = fieldData && typeof fieldData === 'object';
                                    const fieldPath = isObject ? fieldData.fieldPath : fieldData;
                                    const operator = isObject ? fieldData.operator : 'equals';
                                    const matchValue = isObject ? (fieldData.matchValue !== undefined ? fieldData.matchValue : '') : (column.matchValue || '');
                                    
                                    return (
                                        <div key={fIdx} style={{ 
                                            padding: '0.5rem', 
                                            border: '1px solid var(--border)', 
                                            borderRadius: '4px', 
                                            marginBottom: '0.75rem', 
                                            backgroundColor: 'var(--background)' 
                                        }}>
                                            <div style={{ marginBottom: '0.5rem' }}>
                                                <SearchableSelect
                                                    value={fieldPath}
                                                    onChange={(newValue) => {
                                                        const newFields = [...(column.conditionalFields || [''])];
                                                        if (isObject) {
                                                            newFields[fIdx] = { ...fieldData, fieldPath: newValue };
                                                        } else {
                                                            newFields[fIdx] = { fieldPath: newValue, operator: 'equals', matchValue: column.matchValue || '' };
                                                        }
                                                        updateColumnFields(column.id, { conditionalFields: newFields });
                                                    }}
                                                    placeholder="Select field..."
                                                    options={availableFields.map(f => ({ label: f, value: f }))}
                                                />
                                            </div>
                                            <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                                                <div style={{ flex: 1 }}>
                                                    <select
                                                        value={operator}
                                                        onChange={(e) => {
                                                            const newFields = [...(column.conditionalFields || [''])];
                                                            const newOperator = e.target.value;
                                                            if (isObject) {
                                                                newFields[fIdx] = { ...fieldData, operator: newOperator };
                                                            } else {
                                                                newFields[fIdx] = { fieldPath: fieldData, operator: newOperator, matchValue: column.matchValue || '' };
                                                            }
                                                            updateColumnFields(column.id, { conditionalFields: newFields });
                                                        }}
                                                        style={{
                                                            width: '100%',
                                                            padding: '0 0.45rem',
                                                            height: '32px',
                                                            fontSize: '0.8rem',
                                                            border: '1px solid var(--border)',
                                                            borderRadius: '4px',
                                                            backgroundColor: 'var(--background)',
                                                            boxSizing: 'border-box',
                                                            margin: '0'
                                                        }}
                                                    >
                                                        <option value="equals">=</option>
                                                        <option value="not_equals">≠</option>
                                                        <option value="contains">Contains</option>
                                                        <option value="greater_than">&gt;</option>
                                                        <option value="less_than">&lt;</option>
                                                        <option value="greater_than_equal">≥</option>
                                                        <option value="less_than_equal">≤</option>
                                                    </select>
                                                </div>
                                                <div style={{ flex: 2 }}>
                                                    <input
                                                        type="text"
                                                        value={matchValue}
                                                        onChange={(e) => {
                                                            const newFields = [...(column.conditionalFields || [''])];
                                                            const newVal = e.target.value;
                                                            if (isObject) {
                                                                newFields[fIdx] = { ...fieldData, matchValue: newVal };
                                                            } else {
                                                                newFields[fIdx] = { fieldPath: fieldData, matchValue: newVal, operator: 'equals' };
                                                            }
                                                            updateColumnFields(column.id, { conditionalFields: newFields });
                                                        }}
                                                        placeholder="Value"
                                                        style={{
                                                            width: '100%',
                                                            padding: '0 0.45rem',
                                                            height: '32px',
                                                            fontSize: '0.8rem',
                                                            border: '1px solid var(--border)',
                                                            borderRadius: '4px',
                                                            boxSizing: 'border-box'
                                                        }}
                                                    />
                                                </div>
                                                <button 
                                                    type="button"
                                                    onClick={() => {
                                                        const newFields = (column.conditionalFields || ['']).filter((_, i) => i !== fIdx);
                                                        updateColumnFields(column.id, { conditionalFields: newFields.length ? newFields : [''] });
                                                    }}
                                                    style={{ 
                                                        padding: '0', 
                                                        width: '32px', 
                                                        height: '32px', 
                                                        background: 'none', 
                                                        border: '1px solid var(--border)', 
                                                        borderRadius: '4px', 
                                                        cursor: 'pointer',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        color: 'var(--danger)',
                                                        boxSizing: 'border-box'
                                                    }}
                                                    title="Remove rule"
                                                >×</button>
                                            </div>
                                        </div>
                                    );
                                })}
                                <button 
                                    type="button"
                                    onClick={() => {
                                        updateColumn(column.id, 'conditionalFields', [...(column.conditionalFields || ['']), '']);
                                    }}
                                    style={{ 
                                        fontSize: '0.7rem', 
                                        padding: '4px 10px', 
                                        cursor: 'pointer', 
                                        background: 'var(--background)', 
                                        border: '1px solid var(--primary)', 
                                        color: 'var(--primary)', 
                                        borderRadius: '4px',
                                        fontWeight: '500'
                                    }}
                                >+ Add Field</button>
                            </div>


                            <div style={{ marginBottom: '0.75rem' }}>
                                <label style={{ fontSize: '0.7rem', display: 'block', marginBottom: '0.25rem', color: 'var(--text-secondary)' }}>
                                    Result: All Match
                                </label>
                                <input
                                    type="text"
                                    value={column.allMatchResult || ''}
                                    onChange={(e) => updateColumn(column.id, 'allMatchResult', e.target.value)}
                                    placeholder="Fully Enabled"
                                    style={{ 
                                        width: '100%', 
                                        padding: '0.5rem', 
                                        fontSize: '0.875rem', 
                                        border: '1px solid var(--border)', 
                                        borderRadius: '4px',
                                        boxSizing: 'border-box'
                                    }}
                                />
                            </div>
                            <div style={{ marginBottom: '0.75rem' }}>
                                <label style={{ fontSize: '0.7rem', display: 'block', marginBottom: '0.25rem', color: 'var(--text-secondary)' }}>
                                    Result: Some Match
                                </label>
                                <input
                                    type="text"
                                    value={column.someMatchResult || ''}
                                    onChange={(e) => updateColumn(column.id, 'someMatchResult', e.target.value)}
                                    placeholder="Partially Enabled"
                                    style={{ 
                                        width: '100%', 
                                        padding: '0.5rem', 
                                        fontSize: '0.875rem', 
                                        border: '1px solid var(--border)', 
                                        borderRadius: '4px',
                                        boxSizing: 'border-box'
                                    }}
                                />
                            </div>
                            <div>
                                <label style={{ fontSize: '0.7rem', display: 'block', marginBottom: '0.25rem', color: 'var(--text-secondary)' }}>
                                    Result: None Match
                                </label>
                                <input
                                    type="text"
                                    value={column.noneMatchResult || ''}
                                    onChange={(e) => updateColumn(column.id, 'noneMatchResult', e.target.value)}
                                    placeholder="Fully Disabled"
                                    style={{ 
                                        width: '100%', 
                                        padding: '0.5rem', 
                                        fontSize: '0.875rem', 
                                        border: '1px solid var(--border)', 
                                        borderRadius: '4px',
                                        boxSizing: 'border-box'
                                    }}
                                />
                            </div>
                        </div>
                        )}
                    </div>
                ))}

                <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                    <button
                        type="button"
                        onClick={() => autoDetectColumns(false)}
                        disabled={!availableFields || availableFields.length === 0}
                        style={{
                            flex: 1,
                            padding: '0.5rem',
                            background: 'var(--background)',
                            color: 'var(--primary)',
                            border: '1px solid var(--primary)',
                            borderRadius: 'var(--radius)',
                            cursor: 'pointer',
                            fontSize: '0.875rem',
                            fontWeight: '500',
                            opacity: (!metricPath || (!dataContext && !scanData)) ? 0.6 : 1
                        }}
                        title="Automatically create columns from available fields"
                    >
                        Auto-detect Columns
                    </button>
                    <button
                        type="button"
                        onClick={addColumn}
                        style={{
                            flex: 1,
                            padding: '0.5rem',
                            background: 'var(--primary)',
                            color: 'white',
                            border: 'none',
                            borderRadius: 'var(--radius)',
                            cursor: 'pointer',
                            fontSize: '0.875rem',
                            fontWeight: '500'
                        }}
                    >
                        + Add Column
                    </button>
                </div>
            </div>
        </div>
    );
};

export default TableColumnConfiguration;
